import { Link } from "react-router-dom";
import Seo from "../lib/seo";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { CONTACT } from "../lib/constants";

/**
 * Privacy Policy — placeholder structure.
 *
 * NOTE FOR SITE OWNER:
 * A finalized Privacy Policy draft will be provided by the JC Law Foundation.
 * Until then, the copy below is a standard nonprofit template you can review
 * and replace. This copy has NOT been reviewed by an attorney.
 */
export default function PrivacyPolicyPage() {
    const effectiveDate = "January 1, 2026";

    return (
        <>
            <Seo
                title="Privacy Policy"
                description="Read the JC Law Foundation Privacy Policy to learn how information submitted through donations, contact forms, and email subscriptions may be collected and used."
                path="/privacy-policy"
            />

            <PageHeader
                overline="Legal"
                title="Privacy Policy"
                subtitle={`Effective Date: ${effectiveDate}. This policy describes how JC Law Foundation collects, uses, and protects information submitted through our website.`}
            />

            <section className="bg-editorial py-20 md:py-28" data-testid="privacy-policy-content">
                <div className="max-w-[860px] mx-auto px-6 lg:px-10">
                    <Reveal>
                        <div className="bg-editorial-muted border border-editorial-border p-6 md:p-8 mb-10 text-sm text-navy-800/80 leading-relaxed">
                            <p><strong>Notice:</strong> This Privacy Policy is provided for informational purposes only. It has not been reviewed by an attorney and does not constitute legal advice. A finalized version will replace this text.</p>
                        </div>
                    </Reveal>

                    <div className="prose-legal space-y-8 text-navy-900">
                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">1. About JC Law Foundation</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                JC Law Foundation (“JC Law,” “we,” “us,” or “our”) is a registered 501(c)(3) nonprofit organization dedicated to improving cardiac emergency preparedness, increasing AED access, and advocating for lifesaving safety measures in hotels. This Privacy Policy applies to information collected through <a href="https://www.jclawsc.org" className="text-coral hover:underline">jclawsc.org</a> (the “Site”).
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">2. Information We Collect</h2>
                            <p className="text-navy-800/85 leading-relaxed mb-3">We may collect the following categories of information:</p>
                            <ul className="list-disc pl-6 space-y-2 text-navy-800/85 leading-relaxed">
                                <li><strong>Contact information</strong> you submit through our contact form, such as name, email address, subject, and message content.</li>
                                <li><strong>Email subscription information</strong> if you opt in to receive updates from JC Law Foundation.</li>
                                <li><strong>Donation information</strong> processed by our third-party payment partner (Givebutter), which may include name, email address, billing details, and gift amount. We do not directly store payment card numbers.</li>
                                <li><strong>Petition information</strong> submitted on Change.org, which is governed by Change.org’s privacy policy, not this policy.</li>
                                <li><strong>Website usage information</strong> such as pages viewed, referring URL, device type, and general location, collected through standard web analytics.</li>
                            </ul>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">3. How We Use Information</h2>
                            <p className="text-navy-800/85 leading-relaxed mb-3">We use information to:</p>
                            <ul className="list-disc pl-6 space-y-2 text-navy-800/85 leading-relaxed">
                                <li>Respond to inquiries submitted through the Site.</li>
                                <li>Process and acknowledge donations.</li>
                                <li>Send email updates, newsletters, and advocacy alerts to those who have opted in.</li>
                                <li>Improve the Site, our programs, and our communications.</li>
                                <li>Comply with applicable legal requirements.</li>
                            </ul>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">4. Third-Party Services</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                We use trusted third-party providers to help operate the Site, including hosting (Netlify), donation processing (Givebutter), petition hosting (Change.org), and email delivery. These providers process information subject to their own privacy policies.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">5. Cookies and Analytics</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                The Site may use cookies and similar technologies to remember preferences and understand traffic patterns. You can control cookies through your browser settings. Disabling cookies may affect certain features of the Site.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">6. How We Share Information</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                We do not sell your personal information. We may share information with service providers who help us operate the Site and our programs, when required by law, or with your consent.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">7. Your Choices</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                You may unsubscribe from marketing emails at any time using the link in any message we send. You may also contact us to request corrections or deletion of information you have submitted to us directly, subject to applicable law and legitimate recordkeeping needs.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">8. Children’s Privacy</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                The Site is not directed to children under 13, and we do not knowingly collect personal information from children. If you believe a child has provided us information, please contact us so we can delete it.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">9. Security</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                We take reasonable administrative and technical steps to protect information submitted through the Site. No internet transmission is completely secure, however, and we cannot guarantee absolute security.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">10. Changes to This Policy</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                We may update this Privacy Policy from time to time. The “Effective Date” above will reflect the latest revision.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">11. Contact Us</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                Questions about this Privacy Policy? Email{" "}
                                <a href={`mailto:${CONTACT.general}`} className="text-coral hover:underline">{CONTACT.general}</a>{" "}
                                or mail us at {CONTACT.location}.
                            </p>
                        </Reveal>

                        <Reveal>
                            <div className="pt-8 border-t border-editorial-border mt-10 text-sm text-navy-700/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <p>See also our <Link to="/terms-of-use" className="text-coral hover:underline">Terms of Use</Link>.</p>
                                <p>© {new Date().getFullYear()} JC Law Foundation. Registered 501(c)(3) Nonprofit Organization.</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
